const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.85;
const CANVAS_MIME = "image/jpeg";

// The extraction service rejects a request whose image data URIs are too
// large (~700k chars each). Stay well under that: when the first encode is
// oversized, retry smaller before giving up on the image (it still attaches
// to the form untouched — this only affects the vision payload).
const MAX_DATA_URI_CHARS = 600000;
const ENCODE_ATTEMPTS = [
  { maxEdge: MAX_EDGE, quality: JPEG_QUALITY },
  { maxEdge: MAX_EDGE, quality: 0.6 },
  { maxEdge: 960, quality: 0.6 },
  { maxEdge: 640, quality: 0.5 }
];

// Formats <img>/canvas can rasterize; anything else (e.g. TIFF) is skipped
// for the LLM payload but still attaches to the form untouched.
const RASTERIZABLE = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/bmp"
]);

// Keyed by the File itself, not by image id: ids restart at "image_1" for
// every document, so an id-keyed cache would hand one import's image to a
// later import. Keying on the File also lets entries be collected with it.
const cache = new WeakMap(); // File -> Promise<string|null>

/**
 * Downscale a copy of the image for the vision payload only — originals
 * attached to the form are never touched. Results are cached per file.
 * @returns {Promise<string|null>} JPEG data URI, or null if unusable.
 */
export function getImageDataUri(file) {
  if (cache.has(file)) {
    return cache.get(file);
  }
  const promise = prepare(file).catch(() => null);
  cache.set(file, promise);
  return promise;
}

async function prepare(file) {
  if (!RASTERIZABLE.has(file.type)) return null;

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);

    for (const { maxEdge, quality } of ENCODE_ATTEMPTS) {
      const scale = Math.min(
        1,
        maxEdge / Math.max(image.width, image.height)
      );
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(image, 0, 0, width, height);
      const dataUri = canvas.toDataURL(CANVAS_MIME, quality);
      if (dataUri.length <= MAX_DATA_URI_CHARS) return dataUri;
    }
    // every attempt was still oversized — skip this image for extraction
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error(`Could not load image ${url}`));
    image.src = url;
  });
}
